import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import { executeSearchRequest } from 'tio-data/requests/search';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import { templatesIncludeCreditsCapacity } from 'tio-common/utils/tuition-assistance/limit-summary';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import {
  type RecentApplication,
  ALL_APPLICATION_STATES,
  ALL_PROGRAM_INSTANCE_STATES,
  buildType4Instances,
  groupTasApplicationDocs,
  normalizePaymentFields,
} from 'tio-common/utils/tuition-assistance/application-states';

export interface BenefitHistoryModel {
  applications: RecentApplication[];
  programInstances: Array<{
    instanceApplication: RecentApplication;
    childApplications: RecentApplication[];
  }>;
  availableYears: number[];
  currentYear: number;
  dependentsOnly: boolean;
  hasDependentPrograms: boolean;
  employeeId: string;
  /** Credit-capacity programs keep the legacy status-based cards (tree is monetary-only). */
  hasCreditCapacity: boolean;
  limitsStatusCurrent?: LimitsStatusDataSignature;
  limitsStatusNext?: LimitsStatusDataSignature;
}

export default class AuthenticatedTasBenefitHistoryRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  queryParams = {
    dependents: { refreshModel: true },
  };

  async model(params: { dependents?: string }): Promise<BenefitHistoryModel> {
    const dependentsOnly = params.dependents === 'true';
    const currentEmployee = this.sessionContext.currentEmployee;

    // Validate employee exists and has required relationships
    if (!currentEmployee?.id) {
      console.error('No current employee found in session context');
      throw new Error('Employee information not loaded. Please refresh the page.');
    }

    const companyId = currentEmployee.company?.id;
    if (!companyId) {
      console.error('Company ID not available on currentEmployee', {
        employeeId: currentEmployee.id,
        company: currentEmployee.company,
      });
      throw new Error('Company information not loaded. Please refresh the page.');
    }

    const hasDependentPrograms =
      currentEmployee.company?.companySetting?.tuitionAssistance?.isDependentProgramsEnabled ??
      false;

    const { flatApplications, programInstances, availableYears } = await this.fetchBenefitHistory(
      currentEmployee.id,
      companyId,
      { dependentOnly: dependentsOnly }
    );

    // Benefit summary data is hidden in the dependent-only view. Monetary
    // programs render tree-sourced cards that self-fetch from
    // /api/v2/limits/summary; the status endpoint is only fetched for
    // credit-capacity programs, which the limits tree does not model.
    let hasCreditCapacity = false;
    let limitsStatusCurrent: LimitsStatusDataSignature | undefined;
    let limitsStatusNext: LimitsStatusDataSignature | undefined;

    if (!dependentsOnly) {
      const templates = await this.store
        .request(
          query<TASProgramTemplateModel>('tas-program-template', {
            'filter[state]': 'TAS.ProgramTemplateState.LAUNCHED',
            'filter[byEligibility]': currentEmployee.id,
            'filter[company]': companyId,
            'page[limit]': 100,
          })
        )
        .then(({ content }) => (content as { data: TASProgramTemplateModel[] }).data);
      hasCreditCapacity = templatesIncludeCreditsCapacity(templates);

      if (hasCreditCapacity) {
        limitsStatusCurrent = await this.tuitionAssistance.getV2LimitsStatus(currentEmployee);

        if (limitsStatusCurrent?.annual_start_date) {
          const currentStart = new Date(limitsStatusCurrent.annual_start_date);
          const nextDate = new Date(currentStart);
          nextDate.setFullYear(currentStart.getFullYear() + 1);

          limitsStatusNext = await this.tuitionAssistance.getV2LimitsStatus(
            currentEmployee,
            undefined,
            nextDate.toISOString().slice(0, 10)
          );
        }
      }
    }

    const currentYear = new Date().getFullYear();

    return {
      applications: flatApplications,
      programInstances,
      availableYears: availableYears.sort((a, b) => b - a), // Descending
      currentYear,
      dependentsOnly,
      hasDependentPrograms,
      employeeId: currentEmployee.id,
      hasCreditCapacity,
      limitsStatusCurrent,
      limitsStatusNext,
    };
  }

  /**
   * Fetch all benefit history (no date restriction like dashboard)
   * Returns applications and instances grouped by year
   */
  async fetchBenefitHistory(
    employeeId: string,
    companyId: string,
    { dependentOnly = false }: { dependentOnly?: boolean } = {}
  ): Promise<{
    flatApplications: RecentApplication[];
    programInstances: Array<{
      instanceApplication: RecentApplication;
      childApplications: RecentApplication[];
    }>;
    availableYears: number[];
  }> {
    try {
      // Query ALL applications (no date restriction)
      const builder = new OpenSearchQueryBuilder()
        .mustTerm('company_id', companyId)
        .mustTerm('employee_id', employeeId);

      // When viewing dependent history, only return records linked to a dependent
      // Note: nil dependent_id is stored as "" via Ruby .to_s, so we must exclude empty string
      if (dependentOnly) {
        builder.addFilter({
          bool: {
            must_not: [{ term: { tas_program_instance_dependent_id_keyword: '' } }],
          },
        });
      }

      builder
        .addFilter({
          bool: {
            should: [
              { terms: { tas_application_state_keyword: ALL_APPLICATION_STATES } },
              { terms: { tas_program_instance_state_keyword: ALL_PROGRAM_INSTANCE_STATES } },
            ],
            minimum_should_match: 1,
          },
        })
        .setSort('updated_at:desc')
        .setPagination(1, 500); // Increased limit for history

      const query = {
        'model-type': 'TasProgramInstance',
        searchBody: builder.build(),
      };

      const { content: response } = (await this.store.request(executeSearchRequest(query))) as {
        content: OpenSearchResponse<RecentApplication>;
      };

      const responseBuilder = new OpenSearchResponseBuilder<RecentApplication>(response);
      const rawDocs = responseBuilder.getDocuments();
      const allApps = normalizePaymentFields(rawDocs);

      // Collect available years from doc dates. Include the preceding year too so
      // academic-year ranges (e.g. 2025-2026) are available when the last action
      // falls in the second calendar year of the academic period.
      const yearsSet = new Set<number>();
      for (const app of allApps) {
        const dateStr = app.updatedAt || app.createdAt;
        if (dateStr) {
          const calendarYear = new Date(dateStr).getFullYear();
          yearsSet.add(calendarYear);
          yearsSet.add(calendarYear - 1);
        }
      }

      const { flatApps, type4InstanceMap } = groupTasApplicationDocs(allApps);
      const programInstances = buildType4Instances(type4InstanceMap);
      const availableYears = Array.from(yearsSet);

      return { flatApplications: flatApps, programInstances, availableYears };
    } catch (error) {
      console.error('Failed to fetch benefit history:', error);
      return { flatApplications: [], programInstances: [], availableYears: [] };
    }
  }
}
